import { useContext, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
  LinearProgress,
  Typography,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Chip,
  Box,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BoltIcon from "@mui/icons-material/Bolt";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import PsychologyIcon from "@mui/icons-material/Psychology";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GroupsIcon from "@mui/icons-material/Groups";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import BatterySaverIcon from "@mui/icons-material/BatterySaver";

import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useTranslation } from "react-i18next";
import { resolveLabel } from "../utils/labelUtils";
import { AuthContext, useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useTour } from "../context/TourContext";
import Header from "../components/Header";
import BtnAdd from "../components/BtnAdd";
import BtnRm from "../components/BtnRm";
import HandScanIcon from "../components/icons/HandScanIcon";
import DayEventsPanel from "../components/DayEventsPanel";

const SIT_DELTA_KEYS = [
  "us_logique",
  "us_social",
  "us_sensoriel",
  "us_passion",
  "us_motivation",
  "us_reserve",
  "us_stamina",
];

const categorizeSit = (sit) => {
  const hasPos = SIT_DELTA_KEYS.some((k) => sit[k] > 0);
  const hasNeg = SIT_DELTA_KEYS.some((k) => sit[k] < 0);
  if (hasPos && !hasNeg) return "positive";
  if (hasNeg && !hasPos) return "negative";
  return "mixed";
};

const CATEGORY_ORDER = [
  "trajets",
  "travail",
  "détente",
  "maison",
  "famille-amis",
  "autres",
];
const CAT_I18N_KEY = (c) => (c === "détente" ? "detente" : c);
const SUB_RAW_TO_KEY = {
  "soirées": "soirees", "réunions": "reunions", "santé": "sante",
  "sport": "sport", "repas": "repas", "transports": "transports",
  "voiture": "voiture", "protection": "protection",
};
const SUB_I18N_KEY = (s) => SUB_RAW_TO_KEY[s] ?? s;
const POLARITY_CONFIG = [
  {
    key: "positive",
    labelKey: "energyGauges.polarity.positive",
    color: "#86efac",
  },
  { key: "mixed", labelKey: "energyGauges.polarity.mixed", color: "#93c5fd" },
  {
    key: "negative",
    labelKey: "energyGauges.polarity.negative",
    color: "#fca5a5",
  },
];

// Construit l'arbre polarity → category → { _items, _subcats }
const buildSitTree = (situations) => {
  const tree = {};
  for (const sit of situations) {
    const pol = categorizeSit(sit);
    const cat = sit.us_category || "autres";
    const sub = sit.us_subcategory || null;
    if (!tree[pol]) tree[pol] = {};
    if (!tree[pol][cat]) tree[pol][cat] = { _items: [], _subcats: {} };
    if (sub) {
      if (!tree[pol][cat]._subcats[sub]) tree[pol][cat]._subcats[sub] = [];
      tree[pol][cat]._subcats[sub].push(sit);
    } else {
      tree[pol][cat]._items.push(sit);
    }
  }
  return tree;
};

const catCount = (node) =>
  node._items.length +
  Object.values(node._subcats).reduce((s, a) => s + a.length, 0);

// Correspondance situation → gauge (frontend key, max)
const SIT_TO_GAUGE = [
  {
    sitKey: "us_logique",
    gaugeKey: "currentLogique",
    max: 100,
    labelKey: "gauges.logique",
  },
  {
    sitKey: "us_social",
    gaugeKey: "currentSocial",
    max: 100,
    labelKey: "gauges.social",
  },
  {
    sitKey: "us_sensoriel",
    gaugeKey: "currentSensoriel",
    max: 100,
    labelKey: "gauges.sensoriel",
  },
  {
    sitKey: "us_passion",
    gaugeKey: "currentPassion",
    max: 100,
    labelKey: "gauges.passion",
  },
  {
    sitKey: "us_motivation",
    gaugeKey: "currentMotivation",
    max: 100,
    labelKey: "gauges.motivation",
  },
  {
    sitKey: "us_reserve",
    gaugeKey: "currentReserve",
    max: 300,
    labelKey: "gauges.reserve",
  },
  {
    sitKey: "us_stamina",
    gaugeKey: "currentStamina",
    max: 100,
    labelKey: "gauges.stamina",
  },
];

const GAUGE_MIN = -50;

// Multiplicateurs pour la compensation vitale sur overflow
const OVERFLOW_MULTIPLIER = {
  currentStamina: 5,
  currentLogique: 3,
  currentSocial: 3,
  currentSensoriel: 3,
  currentPassion: 3,
  currentMotivation: 3,
};

/**
 * Calcule les nouvelles valeurs de toutes les jauges en appliquant des deltas,
 * avec gestion de la compensation vitale et des règles volonté=0.
 *
 * @param {Object} deltas       - { currentLogique: -20, ... }
 * @param {Object} currentGauges - valeurs courantes des jauges (clés "currentXxx")
 * @param {Object} baseGauges   - profil de base (clés "Logique", "Social", etc.)
 * @param {Object} maxGauges    - max par jauge (clés "Logique", etc.)
 * @returns {Object} updates    - nouvelles valeurs absolues à appliquer
 */
function applyDeltas(deltas, currentGauges, baseGauges, maxGauges) {
  const isVolonteZero = (currentGauges.currentMotivation ?? 0) <= 0;
  const updates = {};
  let vitalDrain = 0;

  // Collecter les deltas négatifs (hors stamina) pour le calcul stamina drain (volonté=0)
  const negForStamina = [];

  for (const [gaugeKey, delta] of Object.entries(deltas)) {
    const baseKey = gaugeKey.replace("current", "");
    const max = maxGauges[baseKey] ?? 100;
    const cur = currentGauges[gaugeKey] ?? 0;

    if (gaugeKey === "currentReserve") {
      // La réserve vitale ne peut pas descendre sous 0 et ne se compense pas elle-même
      updates[gaugeKey] = Math.min(max, Math.max(0, cur + delta));
      continue;
    }

    // Quand volonté=0 : les deltas négatifs coûtent ×3, les positifs restent ×1
    const effectiveDelta = delta < 0 && isVolonteZero ? delta * 3 : delta;
    const newValue = Math.min(max, Math.max(GAUGE_MIN, cur + effectiveDelta));
    updates[gaugeKey] = newValue;

    // Compensation vitale sur overflow (seulement si volonté > 0)
    if (delta < 0 && !isVolonteZero) {
      const additionalNeg = Math.max(
        0,
        Math.max(0, -newValue) - Math.max(0, -cur),
      );
      if (additionalNeg > 0) {
        vitalDrain += additionalNeg * (OVERFLOW_MULTIPLIER[gaugeKey] ?? 3);
      }
    }

    // Collecte pour le drain stamina supplémentaire (volonté=0, hors stamina et vital)
    if (delta < 0 && isVolonteZero && gaugeKey !== "currentStamina") {
      const profileVal = baseGauges[baseKey];
      if (profileVal)
        negForStamina.push({ amount: Math.abs(delta), profile: profileVal });
    }
  }

  // Appliquer le drain vital (compensation overflows)
  if (vitalDrain > 0) {
    const curVital = currentGauges.currentReserve ?? 0;
    updates.currentReserve = Math.max(
      0,
      (updates.currentReserve ?? curVital) - vitalDrain,
    );
  }

  // Drain stamina supplémentaire quand volonté=0
  if (negForStamina.length > 0) {
    const sumDeltas = negForStamina.reduce((s, v) => s + v.amount, 0);
    const sumProfiles = negForStamina.reduce((s, v) => s + v.profile, 0);
    const staminaDrain = Math.round(
      ((sumDeltas / sumProfiles) * 100) / negForStamina.length,
    );
    if (staminaDrain > 0) {
      const curStamina = currentGauges.currentStamina ?? 0;
      updates.currentStamina = Math.max(
        GAUGE_MIN,
        (updates.currentStamina ?? curStamina) - staminaDrain,
      );
    }
  }

  return updates;
}

export default function EnergyGauges() {
  const { state: user } = useContext(AuthContext);
  const { accessibleUsers } = useAuth();
  const { appTheme } = useThemeMode();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { startTour } = useTour();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === "en" ? "en" : "fr";

  const isProche = user?.users_status === "p";
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Pour un proche : sélectionner automatiquement le premier utilisateur accessible
  useEffect(() => {
    if (isProche && accessibleUsers.length > 0 && selectedUser === null) {
      setSelectedUser(accessibleUsers[0]);
    }
  }, [isProche, accessibleUsers]); // eslint-disable-line

  // Un proche est toujours en lecture seule, un utilisateur l'est seulement s'il visionne quelqu'un d'autre
  const isReadOnly = isProche || selectedUser !== null;

  const [currentGauges, setCurrentGauges] = useState({
    currentLogique: 100,
    currentSocial: 100,
    currentSensoriel: 100,
    currentPassion: 100,
    currentMotivation: 100,
    currentReserve: 300,
    currentStamina: 100,
  });

  const maxGauges = {
    Logique: 100,
    Social: 100,
    Sensoriel: 100,
    Passion: 100,
    Motivation: 100,
    Reserve: 300,
    Stamina: 100,
  };

  const [baseGauges, setBaseGauges] = useState({
    Logique: 100,
    Social: 100,
    Sensoriel: 100,
    Passion: 100,
    Motivation: 100,
    Reserve: 300,
    Stamina: 100,
  });

  const [situations, setSituations] = useState([]);
  const [sitOpen, setSitOpen] = useState(false);
  const [sitSearch, setSitSearch] = useState("");
  const [morningStarts, setMorningStarts] = useState([]);
  const [morningOpen, setMorningOpen] = useState(false);
  const [morningSearch, setMorningSearch] = useState("");
  const [selectedMorningStarts, setSelectedMorningStarts] = useState([]);
  const [eventsRefreshKey, setEventsRefreshKey] = useState(0);

  // Rechargement des jauges et du profil de base selon l'utilisateur visualisé
  useEffect(() => {
    // Un proche sans sélection n'a rien à afficher
    if (isProche && !selectedUser) return;

    const uid = selectedUser?.users_ID;
    const gaugesUrl = uid ? `/api/gauges/user/${uid}` : "/api/gauges";
    const defaultsUrl = uid
      ? `/api/gauges/user/${uid}/defaults`
      : "/api/gauges/defaults";

    fetch(defaultsUrl, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setBaseGauges({
          Logique: data.ug_defaultLogique ?? 100,
          Social: data.ug_defaultSocial ?? 100,
          Sensoriel: data.ug_defaultSensoriel ?? 100,
          Passion: data.ug_defaultPassion ?? 100,
          Motivation: data.ug_defaultMotivation ?? 100,
          Reserve: data.ug_defaultReserve ?? 300,
          Stamina: data.ug_defaultStamina ?? 100,
        });
      })
      .catch(() => {});

    fetch(gaugesUrl, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setCurrentGauges({
          currentLogique: data.ug_logique,
          currentSocial: data.ug_social,
          currentSensoriel: data.ug_sensoriel,
          currentPassion: data.ug_passion,
          currentMotivation: data.ug_motivation,
          currentReserve: data.ug_reserve,
          currentStamina: data.ug_stamina,
        });
      })
      .catch(() => {});
  }, [selectedUser]);

  useEffect(() => {
    fetch("/api/situations", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setSituations(data.filter((s) => s.us_active)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/morning-starts", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setMorningStarts(data.filter((ms) => ms.ums_active)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("tourDone")) {
      const t = setTimeout(startTour, 800);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  const handleGaugeUpdate = (manaName, newValue) => {
    const delta = newValue - (currentGauges[manaName] ?? 0);
    const updates = applyDeltas(
      { [manaName]: delta },
      currentGauges,
      baseGauges,
      maxGauges,
    );
    setCurrentGauges((prev) => ({ ...prev, ...updates }));
    fetch("/api/gauges", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }).catch(() => {});
  };

  const MS_TO_GAUGE = [
    {
      msKey: "ums_logique",
      gaugeKey: "currentLogique",
      max: 100,
      labelKey: "gauges.logique",
    },
    {
      msKey: "ums_social",
      gaugeKey: "currentSocial",
      max: 100,
      labelKey: "gauges.social",
    },
    {
      msKey: "ums_sensoriel",
      gaugeKey: "currentSensoriel",
      max: 100,
      labelKey: "gauges.sensoriel",
    },
    {
      msKey: "ums_passion",
      gaugeKey: "currentPassion",
      max: 100,
      labelKey: "gauges.passion",
    },
    {
      msKey: "ums_motivation",
      gaugeKey: "currentMotivation",
      max: 100,
      labelKey: "gauges.motivation",
    },
    {
      msKey: "ums_reserve",
      gaugeKey: "currentReserve",
      max: 300,
      labelKey: "gauges.reserve",
    },
    {
      msKey: "ums_stamina",
      gaugeKey: "currentStamina",
      max: 100,
      labelKey: "gauges.stamina",
    },
  ];

  const toggleMorningStart = (ms) => {
    setSelectedMorningStarts((prev) =>
      prev.find((s) => s.ums_ID === ms.ums_ID)
        ? prev.filter((s) => s.ums_ID !== ms.ums_ID)
        : [...prev, ms],
    );
  };

  const applyMorningStarts = () => {
    const updates = {};
    const next = {};
    const eventDeltas = {};
    const defaultVital = baseGauges.Reserve;
    const vitalThreshold = Math.round(0.25 * defaultVital);

    for (const { msKey, gaugeKey } of MS_TO_GAUGE) {
      const baseKey = gaugeKey.replace("current", "");
      const base = baseGauges[baseKey];
      const max = maxGauges[baseKey];
      const current = currentGauges[gaugeKey];
      const totalDelta = selectedMorningStarts.reduce(
        (sum, ms) => sum + (ms[msKey] ?? 0),
        0,
      );
      if (totalDelta !== 0) eventDeltas[msKey.replace("ums_", "")] = totalDelta;

      if (gaugeKey === "currentReserve") {
        // Réserve : si sous le seuil 25%, le déficit se répercute sur le matin
        const debt = current < vitalThreshold ? current - vitalThreshold : 0;
        next[gaugeKey] = Math.min(max, Math.max(0, base + totalDelta + debt));
      } else {
        // Autres jauges : la valeur négative courante est une dette qui s'ajoute
        const debt = Math.min(0, current);
        next[gaugeKey] = Math.min(
          max,
          Math.max(GAUGE_MIN, base + totalDelta + debt),
        );
      }
      updates[gaugeKey] = next[gaugeKey];
    }
    const wakeLabelFr = selectedMorningStarts.map((ms) => ms.ums_label_fr).filter(Boolean).join(", ");
    const wakeLabelEn = selectedMorningStarts.map((ms) => ms.ums_label_en).filter(Boolean).join(", ");
    setCurrentGauges((prev) => ({ ...prev, ...next }));
    setMorningOpen(false);
    setSelectedMorningStarts([]);

    // Snapshot de fin de journée (hier) avant d'appliquer le morning start
    fetch("/api/history/snapshot", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).catch(() => {});

    fetch("/api/gauges", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }).catch(() => {});

    fetch("/api/day-events", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "morning_start",
        label_fr: wakeLabelFr,
        label_en: wakeLabelEn,
        ...eventDeltas,
      }),
    }).catch(() => {});
    setEventsRefreshKey((k) => k + 1);
  };

  const applySituation = (sit) => {
    setSitOpen(false);
    const deltas = {};
    const eventDeltas = {};
    for (const { sitKey, gaugeKey } of SIT_TO_GAUGE) {
      const delta = sit[sitKey] ?? 0;
      if (delta !== 0) {
        deltas[gaugeKey] = delta;
        eventDeltas[sitKey.replace("us_", "")] = delta;
      }
    }
    const updates = applyDeltas(deltas, currentGauges, baseGauges, maxGauges);
    setCurrentGauges((prev) => ({ ...prev, ...updates }));
    fetch("/api/gauges", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }).catch(() => {});

    fetch("/api/day-events", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "situation",
        label_fr: sit.us_label_fr,
        label_en: sit.us_label_en,
        ...eventDeltas,
      }),
    }).catch(() => {});
    setEventsRefreshKey((k) => k + 1);
  };

  if (!user) return <Navigate to="/connexion" replace />;

  // Proche sans aucun accès accordé encore
  if (isProche && accessibleUsers.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: appTheme.bg,
          color: appTheme.text,
        }}
      >
        <Header />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            gap: 2,
            px: 3,
            textAlign: "center",
          }}
        >
          <VisibilityIcon sx={{ fontSize: 48, color: "#4ade8055" }} />
          <Typography variant="h6" sx={{ color: appTheme.text }}>
            {t("energyGauges.noAccess")}
          </Typography>
          <Typography
            sx={{
              color: appTheme.text + "88",
              fontSize: "0.9rem",
              maxWidth: 400,
            }}
          >
            {t("energyGauges.noAccessDesc")}
          </Typography>
        </Box>
      </div>
    );
  }

  const clamp = (cur, max) => Math.min(100, Math.max(0, (cur / max) * 100));

  const clampedLogique = clamp(currentGauges.currentLogique, maxGauges.Logique);
  const clampedSocial = clamp(currentGauges.currentSocial, maxGauges.Social);
  const clampedSensoriel = clamp(
    currentGauges.currentSensoriel,
    maxGauges.Sensoriel,
  );
  const clampedPassion = clamp(currentGauges.currentPassion, maxGauges.Passion);
  const clampedMotivation = clamp(
    currentGauges.currentMotivation,
    maxGauges.Motivation,
  );

  // Affiche les deltas d'une situation sous forme de chips colorés
  const DeltaChips = ({ sit }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.4 }}>
      {SIT_TO_GAUGE.map(({ sitKey, labelKey }) => {
        const delta = sit[sitKey];
        if (delta === 0) return null;
        return (
          <Chip
            key={sitKey}
            label={`${t(labelKey)} ${delta > 0 ? "+" : ""}${delta}`}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 18,
              backgroundColor: delta > 0 ? "#166534" : "#7f1d1d",
              color: delta > 0 ? "#4ade80" : "#f87171",
            }}
          />
        );
      })}
    </Box>
  );

  // Bouton flottant "Situation"
  const SituationBtn = () => (
    <IconButton
      data-tour="tour-situation-btn"
      onClick={() => {
        setSitOpen(true);
        setSitSearch("");
      }}
      title={t("energyGauges.applyBtn")}
      sx={{
        position: "fixed",
        bottom: isMobile ? "2dvh" : "3vh",
        right: isMobile ? "3dvw" : "2vw",
        zIndex: 1200,
        backgroundColor: "#1e3a5f",
        color: "#93c5fd",
        width: isMobile ? 48 : 56,
        height: isMobile ? 48 : 56,
        boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
        "&:hover": { backgroundColor: "#1e40af" },
      }}
    >
      <BoltIcon fontSize={isMobile ? "small" : "medium"} />
    </IconButton>
  );

  // Bouton flottant "Point de départ de la journée"
  const MorningStartBtn = () => (
    <IconButton
      data-tour="tour-morning-btn"
      onClick={() => {
        setMorningOpen(true);
        setMorningSearch("");
      }}
      title={t("energyGauges.morningBtn")}
      sx={{
        position: "fixed",
        bottom: isMobile ? "calc(2dvh + 56px)" : "calc(3vh + 64px)",
        right: isMobile ? "3dvw" : "2vw",
        zIndex: 1200,
        backgroundColor: "#1a3320",
        color: "#86efac",
        width: isMobile ? 48 : 56,
        height: isMobile ? 48 : 56,
        boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
        "&:hover": { backgroundColor: "#14532d" },
      }}
    >
      <WbSunnyIcon fontSize={isMobile ? "small" : "medium"} />
    </IconButton>
  );

  return (
    <div
      style={isMobile ? {
        backgroundColor: appTheme.bg,
        color: appTheme.text,
        transition: "background-color 0.3s ease, color 0.3s ease",
        fontFamily: "inherit",
      } : {
        backgroundColor: appTheme.bg,
        color: appTheme.text,
        transition: "background-color 0.3s ease, color 0.3s ease",
        fontFamily: "inherit",
      }}
    >
      <Header />

      {/* ── Sélecteur d'utilisateur ── */}
      {/* Visible pour les proches (toujours) ou les utilisateurs avec des accès */}
      {(isProche || accessibleUsers.length > 0) && (
        <Box
          sx={isMobile ? {
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: isReadOnly ? "#0d2010" : "#0f172a",
            borderBottom: `1px solid ${isReadOnly ? "#4ade8066" : "#1e3a5f"}`,
            px: 1.5,
            py: 0.75,
            flexShrink: 0,
          } : {
            position: "fixed",
            top: 68,
            left: 12,
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: isReadOnly ? "#0d2010" : "#0f172a",
            border: `1px solid ${isReadOnly ? "#4ade8066" : "#1e3a5f"}`,
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
          }}
        >
          {isReadOnly && (
            <VisibilityIcon sx={{ color: "#4ade80", fontSize: "0.9rem" }} />
          )}
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            sx={{
              color: isReadOnly ? "#4ade80" : "#93c5fd",
              textTransform: "none",
              fontSize: "0.85rem",
              minWidth: 0,
              p: 0.5,
            }}
          >
            {selectedUser
              ? selectedUser.users_pseudo
              : t("energyGauges.me", { pseudo: user?.users_pseudo })}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: { backgroundColor: "#0f172a", color: "#e2e8f0" },
            }}
          >
            {/* "Moi" uniquement pour les utilisateurs (pas les proches) */}
            {!isProche && (
              <MenuItem
                onClick={() => {
                  setSelectedUser(null);
                  setAnchorEl(null);
                }}
                selected={!selectedUser}
                sx={{
                  fontSize: "0.9rem",
                  color: !selectedUser ? "#93c5fd" : "#e2e8f0",
                }}
              >
                {t("energyGauges.me", { pseudo: user?.users_pseudo })}
              </MenuItem>
            )}
            {accessibleUsers.map((u) => (
              <MenuItem
                key={u.users_ID}
                onClick={() => {
                  setSelectedUser(u);
                  setAnchorEl(null);
                }}
                selected={selectedUser?.users_ID === u.users_ID}
                sx={{
                  fontSize: "0.9rem",
                  color:
                    selectedUser?.users_ID === u.users_ID
                      ? "#4ade80"
                      : "#e2e8f0",
                }}
              >
                {u.users_pseudo}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {/* ── Dialog point de départ de la journée ── */}
      <Dialog
        open={morningOpen}
        onClose={() => {
          setMorningOpen(false);
          setSelectedMorningStarts([]);
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#0f172a",
            color: "#e2e8f0",
            minWidth: 300,
            maxWidth: 480,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WbSunnyIcon sx={{ color: "#86efac" }} />
            {t("energyGauges.morningBtn")}
          </Box>
          <IconButton
            onClick={() => {
              setMorningOpen(false);
              setSelectedMorningStarts([]);
            }}
            size="small"
            sx={{ color: "#94a3b8" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {morningStarts.length === 0 ? (
            <Typography sx={{ color: "#64748b", fontSize: "0.85rem", py: 1 }}>
              {t("energyGauges.noMorning")}
            </Typography>
          ) : (
            <>
              <TextField
                size="small"
                placeholder={t("energyGauges.searchPlaceholder")}
                value={morningSearch}
                onChange={(e) => setMorningSearch(e.target.value)}
                autoFocus
                sx={{
                  mb: 1.5,
                  width: "100%",
                  "& input": {
                    color: "#e2e8f0",
                    fontSize: "0.8rem",
                    padding: "6px 10px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#475569",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    { borderColor: "#94a3b8" },
                }}
              />
              <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", mb: 1 }}>
                {t("energyGauges.morningDialogDesc")}
              </Typography>
              <List dense>
                {morningStarts
                  .filter((ms) => {
                    const q = morningSearch.trim().toLowerCase();
                    if (!q) return true;
                    if (
                      resolveLabel(ms, "ums_label", lang)
                        .toLowerCase()
                        .includes(q)
                    )
                      return true;
                    const matchedGauges = MS_TO_GAUGE.filter((g) =>
                      t(g.labelKey).toLowerCase().includes(q),
                    );
                    if (matchedGauges.length > 0)
                      return matchedGauges.some((g) => ms[g.msKey] !== 0);
                    return false;
                  })
                  .map((ms) => {
                    const isSelected = !!selectedMorningStarts.find(
                      (s) => s.ums_ID === ms.ums_ID,
                    );
                    return (
                      <ListItemButton
                        key={ms.ums_ID}
                        onClick={() => toggleMorningStart(ms)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          backgroundColor: isSelected
                            ? "#14532d44"
                            : "transparent",
                          border: isSelected
                            ? "1px solid #4ade8055"
                            : "1px solid transparent",
                          "&:hover": { backgroundColor: "#14532d" },
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{
                            color: "#86efac",
                            p: 0,
                            mr: 1,
                            "&.Mui-checked": { color: "#4ade80" },
                          }}
                        />
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                color: "#e2e8f0",
                              }}
                            >
                              {resolveLabel(ms, "ums_label", lang)}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.4,
                                mt: 0.4,
                              }}
                            >
                              {MS_TO_GAUGE.map(({ msKey, labelKey }) => {
                                const delta = ms[msKey];
                                if (delta === 0) return null;
                                return (
                                  <Chip
                                    key={msKey}
                                    label={`${t(labelKey)} ${delta > 0 ? "+" : ""}${delta}`}
                                    size="small"
                                    sx={{
                                      fontSize: "0.65rem",
                                      height: 18,
                                      backgroundColor:
                                        delta > 0 ? "#166534" : "#7f1d1d",
                                      color: delta > 0 ? "#4ade80" : "#f87171",
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
              </List>

              {selectedMorningStarts.length > 0 && (
                <>
                  <Divider sx={{ borderColor: "#1e293b", my: 1 }} />
                  <Typography
                    sx={{ color: "#94a3b8", fontSize: "0.75rem", mb: 0.5 }}
                  >
                    {t("energyGauges.morningResult")}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {MS_TO_GAUGE.map(({ msKey, gaugeKey, labelKey }) => {
                      const baseKey = gaugeKey.replace("current", "");
                      const base = baseGauges[baseKey];
                      const max = maxGauges[baseKey];
                      const totalDelta = selectedMorningStarts.reduce(
                        (sum, ms) => sum + (ms[msKey] ?? 0),
                        0,
                      );
                      const result = Math.min(
                        max,
                        Math.max(0, base + totalDelta),
                      );
                      return (
                        <Chip
                          key={msKey}
                          label={`${t(labelKey)}: ${result}`}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            backgroundColor: "#1e3a5f",
                            color: "#93c5fd",
                          }}
                        />
                      );
                    })}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        {morningStarts.length > 0 && (
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button
              onClick={() => {
                setMorningOpen(false);
                setSelectedMorningStarts([]);
              }}
              sx={{ color: "#94a3b8", fontSize: "0.8rem" }}
            >
              {t("common.cancelBtn")}
            </Button>
            <Button
              variant="contained"
              disabled={selectedMorningStarts.length === 0}
              onClick={applyMorningStarts}
              sx={{
                backgroundColor: "#166534",
                color: "#4ade80",
                fontSize: "0.8rem",
                "&:hover": { backgroundColor: "#14532d" },
                "&.Mui-disabled": {
                  backgroundColor: "#1e293b",
                  color: "#4b5563",
                },
              }}
            >
              {t("energyGauges.startDay")}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ── Dialog sélection de situation ── */}
      {(() => {
        const filteredSits =
          sitSearch.trim() === ""
            ? situations
            : situations.filter((sit) => {
                const q = sitSearch.trim().toLowerCase();
                if (
                  resolveLabel(sit, "us_label", lang).toLowerCase().includes(q)
                )
                  return true;
                const matchedGauges = SIT_TO_GAUGE.filter((g) =>
                  t(g.labelKey).toLowerCase().includes(q),
                );
                if (matchedGauges.length > 0)
                  return matchedGauges.some((g) => sit[g.sitKey] !== 0);
                return false;
              });
        const sitTree = buildSitTree(filteredSits);
        const renderItems = (items) => (
          <List dense sx={{ p: 0 }}>
            {items.map((sit) => (
              <ListItemButton
                key={sit.us_ID}
                onClick={() => applySituation(sit)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": { backgroundColor: "#1e3a5f" },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        color: "#e2e8f0",
                      }}
                    >
                      {resolveLabel(sit, "us_label", lang)}
                    </Typography>
                  }
                  secondary={<DeltaChips sit={sit} />}
                />
              </ListItemButton>
            ))}
          </List>
        );
        const renderCatNode = (cat, node) => {
          const count = catCount(node);
          const orderedSubcats = Object.keys(node._subcats).sort();
          return (
            <Accordion
              key={cat + String(!!sitSearch.trim())}
              defaultExpanded={!!sitSearch.trim()}
              sx={{
                backgroundColor: "#0a1628",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: "#64748b", fontSize: "1rem" }} />
                }
                sx={{
                  minHeight: 36,
                  "& .MuiAccordionSummary-content": { my: 0.5 },
                }}
              >
                <Typography
                  sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#94a3b8" }}
                >
                  {t("categories." + CAT_I18N_KEY(cat))}{" "}
                  <span style={{ color: "#475569" }}>({count})</span>
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pl: 1 }}>
                {node._items.length > 0 && renderItems(node._items)}
                {orderedSubcats.map((sub) => (
                  <Accordion
                    key={sub + String(!!sitSearch.trim())}
                    defaultExpanded={!!sitSearch.trim()}
                    sx={{
                      backgroundColor: "#0a1628",
                      boxShadow: "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ color: "#475569", fontSize: "0.9rem" }}
                        />
                      }
                      sx={{
                        minHeight: 32,
                        "& .MuiAccordionSummary-content": { my: 0.25 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                          color: "#64748b",
                        }}
                      >
                        {t("subcategories." + SUB_I18N_KEY(sub), { defaultValue: sub })} ({node._subcats[sub].length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pl: 1 }}>
                      {renderItems(node._subcats[sub])}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        };
        const allCats = (catMap) => [
          ...CATEGORY_ORDER.filter((c) => catMap[c]),
          ...Object.keys(catMap)
            .filter((c) => !CATEGORY_ORDER.includes(c))
            .sort(),
        ];
        return (
          <Dialog
            open={sitOpen}
            onClose={() => setSitOpen(false)}
            maxWidth={false}
            PaperProps={{
              sx: {
                backgroundColor: "#0f172a",
                color: "#e2e8f0",
                width: isMobile ? "95vw" : "520px",
                maxWidth: "95vw",
                maxHeight: "80vh",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BoltIcon sx={{ color: "#93c5fd" }} />
                {t("energyGauges.applyBtn")}
              </Box>
              <IconButton
                onClick={() => setSitOpen(false)}
                size="small"
                sx={{ color: "#94a3b8" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0, overflowY: "auto" }}>
              <TextField
                size="small"
                placeholder={t("energyGauges.searchPlaceholder")}
                value={sitSearch}
                onChange={(e) => setSitSearch(e.target.value)}
                autoFocus
                sx={{
                  mb: 1.5,
                  width: "100%",
                  "& input": {
                    color: "#e2e8f0",
                    fontSize: "0.8rem",
                    padding: "6px 10px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#475569",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    { borderColor: "#94a3b8" },
                }}
              />
              {situations.length === 0 ? (
                <Typography
                  sx={{ color: "#64748b", fontSize: "0.85rem", py: 1 }}
                >
                  {t("energyGauges.noSituation")}
                </Typography>
              ) : filteredSits.length === 0 ? (
                <Typography
                  sx={{ color: "#64748b", fontSize: "0.85rem", py: 1 }}
                >
                  {t("energyGauges.noResult", { query: sitSearch })}
                </Typography>
              ) : (
                <Box>
                  {POLARITY_CONFIG.map(
                    ({ key: polKey, labelKey: polLabelKey, color }) => {
                      const catMap = sitTree[polKey] ?? {};
                      const total = allCats(catMap).reduce(
                        (s, c) => s + catCount(catMap[c]),
                        0,
                      );
                      if (total === 0) return null;
                      return (
                        <Accordion
                          key={polKey + String(!!sitSearch.trim())}
                          defaultExpanded={
                            !!sitSearch.trim() || polKey === "positive"
                          }
                          sx={{
                            backgroundColor: "#1e293b",
                            color: "#e2e8f0",
                            mb: 1,
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color }} />}
                          >
                            <Typography
                              sx={{
                                color,
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                              }}
                            >
                              {t(polLabelKey)}{" "}
                              <span style={{ opacity: 0.7 }}>({total})</span>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, pl: 1 }}>
                            {allCats(catMap).map((cat) =>
                              renderCatNode(cat, catMap[cat]),
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    },
                  )}
                </Box>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}

      <div style={{ padding: isMobile ? 0 : "10px" }}>
        {/* ══ VERSION DESKTOP ══ */}
        {!isMobile && (
          <div>
            {/* Mana Air */}
            <div
              style={{
                position: "fixed",
                display: "flex",
                left: "5vw",
                top: "10vh",
                flexDirection: "row",
                justifyContent: "center",
                width: "auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  display: "flex",
                  left: "26vw",
                  top: "15vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  position="fixed"
                  top="calc(30vh + 7.5vw)"
                  left="32.5vw"
                  sx={{
                    color: "lightblue",
                    transform: "translateX(-50%)",
                    textAlign: "center",
                  }}
                >
                  <PsychologyIcon />
                  <br />
                  {currentGauges.currentLogique} pts
                </Typography>
                <div
                  data-tour="tour-gauge-air"
                  style={{
                    position: "fixed",
                    top: "25vh",
                    left: "25vw",
                    height: "1vh",
                    width: "15vw",
                    rotate: "-90deg",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    color="success"
                    variant="determinate"
                    value={clampedLogique}
                    sx={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "25px",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#14b8a6",
                      },
                    }}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      position: "fixed",
                      left: "28.5vw",
                      top: "18vh",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5rem",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.logiqueName")}
                      mana={currentGauges.currentLogique}
                      manaName="currentLogique"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.logiqueName")}
                      mana={currentGauges.currentLogique}
                      manaName="currentLogique"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Mana Eau */}
            <div
              style={{
                position: "fixed",
                display: "flex",
                left: "34vw",
                top: "10vh",
                flexDirection: "row",
                justifyContent: "center",
                width: "auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  display: "flex",
                  left: "34vw",
                  top: "15vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  position="fixed"
                  top="calc(30vh + 7.5vw)"
                  left="40.5vw"
                  sx={{
                    color: "lightblue",
                    transform: "translateX(-50%)",
                    textAlign: "center",
                  }}
                >
                  <GroupsIcon />
                  <br />
                  {currentGauges.currentSocial} pts
                </Typography>
                <div
                  data-tour="tour-gauge-eau"
                  style={{
                    position: "fixed",
                    top: "25vh",
                    left: "33vw",
                    height: "1vh",
                    width: "15vw",
                    rotate: "-90deg",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    color="info"
                    variant="determinate"
                    value={clampedSocial}
                    sx={{ height: "100%", width: "100%", borderRadius: "25px" }}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      position: "fixed",
                      left: "36.5vw",
                      top: "18vh",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5rem",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.socialName")}
                      mana={currentGauges.currentSocial}
                      manaName="currentSocial"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.socialName")}
                      mana={currentGauges.currentSocial}
                      manaName="currentSocial"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Mana Terre */}
            <div
              style={{
                position: "fixed",
                display: "flex",
                left: "42vw",
                top: "10vh",
                flexDirection: "row",
                justifyContent: "center",
                width: "auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  display: "flex",
                  left: "42vw",
                  top: "15vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  position="fixed"
                  left="48.5vw"
                  top="calc(30vh + 7.5vw)"
                  sx={{
                    color: "lightblue",
                    transform: "translateX(-50%)",
                    textAlign: "center",
                  }}
                >
                  <HandScanIcon />
                  <br />
                  {currentGauges.currentSensoriel} pts
                </Typography>
                <div
                  data-tour="tour-gauge-terre"
                  style={{
                    position: "fixed",
                    top: "25vh",
                    left: "41vw",
                    height: "1vh",
                    width: "15vw",
                    rotate: "-90deg",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    color="warning"
                    variant="determinate"
                    value={clampedSensoriel}
                    sx={{ height: "100%", width: "100%", borderRadius: "25px" }}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      position: "fixed",
                      left: "44.5vw",
                      top: "18vh",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5rem",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.sensorielName")}
                      mana={currentGauges.currentSensoriel}
                      manaName="currentSensoriel"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.sensorielName")}
                      mana={currentGauges.currentSensoriel}
                      manaName="currentSensoriel"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Mana Feu */}
            <div
              style={{
                position: "fixed",
                display: "flex",
                left: "50vw",
                top: "10vh",
                flexDirection: "row",
                justifyContent: "center",
                width: "auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  display: "flex",
                  left: "50vw",
                  top: "15vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  position="fixed"
                  left="56.5vw"
                  top="calc(30vh + 7.5vw)"
                  sx={{
                    color: "lightblue",
                    transform: "translateX(-50%)",
                    textAlign: "center",
                  }}
                >
                  <FavoriteIcon />
                  <br />
                  {currentGauges.currentPassion} pts
                </Typography>
                <div
                  data-tour="tour-gauge-feu"
                  style={{
                    position: "fixed",
                    top: "25vh",
                    left: "49vw",
                    height: "1vh",
                    width: "15vw",
                    rotate: "-90deg",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    color="error"
                    variant="determinate"
                    value={clampedPassion}
                    sx={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "25px",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#fb7185",
                      },
                      backgroundColor: "#991b1b",
                    }}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      position: "fixed",
                      left: "52.5vw",
                      top: "18vh",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5rem",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.passionName")}
                      mana={currentGauges.currentPassion}
                      manaName="currentPassion"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.passionName")}
                      mana={currentGauges.currentPassion}
                      manaName="currentPassion"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Mana Volonté */}
            <div
              style={{
                position: "fixed",
                display: "flex",
                left: "58vw",
                top: "10vh",
                flexDirection: "row",
                justifyContent: "center",
                width: "auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  display: "flex",
                  left: "58vw",
                  top: "15vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  position="fixed"
                  left="64.5vw"
                  top="calc(30vh + 7.5vw)"
                  sx={{
                    color: "lightblue",
                    transform: "translateX(-50%)",
                    textAlign: "center",
                  }}
                >
                  <LocalFireDepartmentIcon />
                  <br />
                  {currentGauges.currentMotivation} pts
                </Typography>
                <div
                  data-tour="tour-gauge-volonte"
                  style={{
                    position: "fixed",
                    top: "25vh",
                    left: "57vw",
                    height: "1vh",
                    width: "15vw",
                    rotate: "-90deg",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={clampedMotivation}
                    sx={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "25px",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#a855f7",
                      },
                      backgroundColor: "#6b21a8",
                    }}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      position: "fixed",
                      left: "60.5vw",
                      top: "18vh",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5rem",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.motivationName")}
                      mana={currentGauges.currentMotivation}
                      manaName="currentMotivation"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.motivationName")}
                      mana={currentGauges.currentMotivation}
                      manaName="currentMotivation"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Mana Vital */}
            <div
              data-tour="tour-gauge-vital"
              style={{
                position: "fixed",
                bottom: "8vh",
                left: "29vw",
                width: "16vw",
                height: "22vh",
              }}
            >
              <CircularProgressbar
                value={
                  (currentGauges.currentReserve / maxGauges.Reserve) * 100
                }
                circleRatio={0.5}
                text={`${currentGauges.currentReserve} points`}
                styles={buildStyles({
                  rotation: 0.75,
                  strokeLinecap: "butt",
                  textSize: "1em",
                  pathTransitionDuration: 0.3,
                  pathColor: "#f44336",
                  textColor: "#f88",
                  trailColor: "#af8c8d",
                })}
              />
              <Typography
                variant="h5"
                position="fixed"
                left="37vw"
                top="78vh"
                sx={{ color: "lightblue", transform: "translateX(-50%)" }}
                textAlign="center"
              >
                <BatterySaverIcon />
              </Typography>
              {!isReadOnly && (
                <div
                  style={{
                    position: "fixed",
                    left: "37vw",
                    bottom: "2vh",
                    display: "flex",
                    flexDirection: "row",
                    gap: "5rem",
                    color: "lightblue",
                    transform: "translateX(-50%)",
                  }}
                >
                  <BtnAdd
                    name={t("gauges.reserveName")}
                    mana={currentGauges.currentReserve}
                    manaName="currentReserve"
                    onGaugeUpdate={handleGaugeUpdate}
                  />
                  <BtnRm
                    name={t("gauges.reserveName")}
                    mana={currentGauges.currentReserve}
                    manaName="currentReserve"
                    onGaugeUpdate={handleGaugeUpdate}
                  />
                </div>
              )}
            </div>

            {/* Stamina */}
            <div
              data-tour="tour-gauge-stamina"
              style={{
                position: "fixed",
                bottom: "8vh",
                left: "52vw",
                width: "16vw",
                height: "22vh",
              }}
            >
              <CircularProgressbar
                value={(currentGauges.currentStamina / maxGauges.Stamina) * 100}
                circleRatio={0.5}
                text={`${currentGauges.currentStamina} points`}
                styles={buildStyles({
                  rotation: 0.75,
                  strokeLinecap: "butt",
                  textSize: "1em",
                  pathTransitionDuration: 0.3,
                  pathColor: "#42d750",
                  textColor: "#f88",
                  trailColor: "#cfe9d2",
                })}
              />
              <Typography
                variant="h5"
                position="fixed"
                left="60vw"
                top="78vh"
                sx={{ color: "lightblue", transform: "translateX(-50%)" }}
                textAlign="center"
              >
                <DirectionsRunIcon />
              </Typography>
              {!isReadOnly && (
                <div
                  style={{
                    position: "fixed",
                    left: "60vw",
                    bottom: "2vh",
                    display: "flex",
                    flexDirection: "row",
                    gap: "5rem",
                    color: "lightblue",
                    transform: "translateX(-50%)",
                  }}
                >
                  <BtnAdd
                    name={t("gauges.staminaName")}
                    mana={currentGauges.currentStamina}
                    manaName="currentStamina"
                    onGaugeUpdate={handleGaugeUpdate}
                  />
                  <BtnRm
                    name={t("gauges.staminaName")}
                    mana={currentGauges.currentStamina}
                    manaName="currentStamina"
                    onGaugeUpdate={handleGaugeUpdate}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ VERSION MOBILE ══ */}
        {isMobile && (
          <div
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              padding: "1rem 0",
              paddingBottom: "6rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "3.5rem",
            }}
          >
            {/* Ligne 1 : Air, Eau, Terre */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                width: "100%",
                maxWidth: "420px",
              }}
            >
              {[
                {
                  nameKey: "gauges.logiqueName",
                  manaName: "currentLogique",
                  icon: <PsychologyIcon />,
                  value: currentGauges.currentLogique,
                  clamped: clampedLogique,
                  color: "success",
                  sx: {
                    "& .MuiLinearProgress-bar": { backgroundColor: "#14b8a6" },
                  },
                },
                {
                  nameKey: "gauges.socialName",
                  manaName: "currentSocial",
                  icon: <GroupsIcon />,
                  value: currentGauges.currentSocial,
                  clamped: clampedSocial,
                  color: "info",
                },
                {
                  nameKey: "gauges.sensorielName",
                  manaName: "currentSensoriel",
                  icon: <HandScanIcon />,
                  value: currentGauges.currentSensoriel,
                  clamped: clampedSensoriel,
                  color: "warning",
                },
              ].map(
                ({
                  nameKey,
                  manaName,
                  icon,
                  value,
                  clamped,
                  color,
                  sx: extraSx,
                }) => (
                  <div
                    key={manaName}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      width: "30%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 15,
                          width: "50px",
                        }}
                      >
                        <div
                          style={{
                            alignSelf: "center",
                            width: "8px",
                            height: "35dvw",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={clamped}
                            color={color}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              width: "35dvw",
                              height: "8px",
                              borderRadius: "25px",
                              transform: "translate(-50%, -50%) rotate(-90deg)",
                              ...extraSx,
                            }}
                          />
                        </div>
                        <Typography
                          sx={{
                            whiteSpace: "nowrap",
                            color: "lightblue",
                            textAlign: "center",
                            width: "100%",
                            fontSize: "1.2rem",
                          }}
                        >
                          {icon}
                          <br />
                          {`${value} pts`}
                        </Typography>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            color: "lightblue",
                          }}
                        >
                          {!isReadOnly && (
                            <>
                              <BtnAdd
                                name={t(nameKey)}
                                mana={value}
                                manaName={manaName}
                                onGaugeUpdate={handleGaugeUpdate}
                              />
                              <BtnRm
                                name={t(nameKey)}
                                mana={value}
                                manaName={manaName}
                                onGaugeUpdate={handleGaugeUpdate}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* Ligne 2 : Feu, Volonté */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                width: "100%",
                maxWidth: "420px",
              }}
            >
              {[
                {
                  nameKey: "gauges.passionName",
                  manaName: "currentPassion",
                  icon: <FavoriteIcon />,
                  value: currentGauges.currentPassion,
                  clamped: clampedPassion,
                  color: "error",
                  sx: {
                    "& .MuiLinearProgress-bar": { backgroundColor: "#fb7185" },
                  },
                },
                {
                  nameKey: "gauges.motivationName",
                  manaName: "currentMotivation",
                  icon: <LocalFireDepartmentIcon />,
                  value: currentGauges.currentMotivation,
                  clamped: clampedMotivation,
                  color: "primary",
                  sx: {
                    "& .MuiLinearProgress-bar": { backgroundColor: "#a855f7" },
                    backgroundColor: "#6b21a8",
                  },
                },
              ].map(
                ({
                  nameKey,
                  manaName,
                  icon,
                  value,
                  clamped,
                  color,
                  sx: extraSx,
                }) => (
                  <div
                    key={manaName}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      width: "30%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 15,
                          width: "50px",
                        }}
                      >
                        <div
                          style={{
                            alignSelf: "center",
                            width: "8px",
                            height: "35dvw",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={clamped}
                            color={color}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              width: "35dvw",
                              height: "8px",
                              borderRadius: "25px",
                              transform: "translate(-50%, -50%) rotate(-90deg)",
                              ...extraSx,
                            }}
                          />
                        </div>
                        <Typography
                          sx={{
                            whiteSpace: "nowrap",
                            color: "lightblue",
                            textAlign: "center",
                            width: "100%",
                            fontSize: "1.2rem",
                          }}
                        >
                          {icon}
                          <br />
                          {`${value} pts`}
                        </Typography>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            color: "lightblue",
                          }}
                        >
                          {!isReadOnly && (
                            <>
                              <BtnAdd
                                name={t(nameKey)}
                                mana={value}
                                manaName={manaName}
                                onGaugeUpdate={handleGaugeUpdate}
                              />
                              <BtnRm
                                name={t(nameKey)}
                                mana={value}
                                manaName={manaName}
                                onGaugeUpdate={handleGaugeUpdate}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* Circulaires */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2rem",
                width: "100%",
                maxWidth: "420px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ position: "relative", width: "60dvw" }}>
                  <CircularProgressbar
                    value={
                      (currentGauges.currentReserve / maxGauges.Reserve) *
                      100
                    }
                    circleRatio={0.5}
                    text={`${currentGauges.currentReserve} pts`}
                    styles={buildStyles({
                      rotation: 0.75,
                      strokeLinecap: "butt",
                      textSize: "1em",
                      pathColor: "red",
                      textColor: "#f88",
                      trailColor: "#af8c8d",
                    })}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "72%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "lightblue",
                      fontSize: "1.8rem",
                      lineHeight: 1,
                    }}
                  >
                    <BatterySaverIcon fontSize="inherit" />
                  </div>
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.reserveName")}
                      mana={currentGauges.currentReserve}
                      manaName="currentReserve"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.reserveName")}
                      mana={currentGauges.currentReserve}
                      manaName="currentReserve"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ position: "relative", width: "60dvw" }}>
                  <CircularProgressbar
                    value={
                      (currentGauges.currentStamina / maxGauges.Stamina) * 100
                    }
                    circleRatio={0.5}
                    text={`${currentGauges.currentStamina} pts`}
                    styles={buildStyles({
                      rotation: 0.75,
                      strokeLinecap: "butt",
                      textSize: "1em",
                      pathColor: "#14b8a6",
                      textColor: "#f88",
                      trailColor: "#cfe9d2",
                    })}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "72%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "lightblue",
                      fontSize: "1.8rem",
                      lineHeight: 1,
                    }}
                  >
                    <DirectionsRunIcon fontSize="inherit" />
                  </div>
                </div>
                {!isReadOnly && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      color: "lightblue",
                    }}
                  >
                    <BtnAdd
                      name={t("gauges.staminaName")}
                      mana={currentGauges.currentStamina}
                      manaName="currentStamina"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                    <BtnRm
                      name={t("gauges.staminaName")}
                      mana={currentGauges.currentStamina}
                      manaName="currentStamina"
                      onGaugeUpdate={handleGaugeUpdate}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Boutons flottants — masqués en lecture seule */}
      {!isReadOnly && <MorningStartBtn />}
      {!isReadOnly && <SituationBtn />}
      {!isReadOnly && <DayEventsPanel refreshTrigger={eventsRefreshKey} />}
    </div>
  );
}
